import React, { useState, useEffect } from 'react';
import { Heart, CheckCircle, Clock, Users, XCircle, AlertCircle, ArrowLeft, MailCheck, Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import './pStyles/ApplicationStatus.css';
import axios from 'axios';
import { checkApplicationStatus, getApplicationStatus, resendApplicationEmail } from '../../services/api';



interface ApplicationStatusResponse {
  application_status: 'pending' | 'approved' | 'rejected';
  status_updated_at?: string;
}
interface NotificationCheckResponse {
  exists: boolean;
}
interface StatusStep {
  id: number;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'upcoming';
  icon: React.ComponentType<{ className?: string }>;
  timestamp?: string;
  estimatedTime?: string;
}

const ApplicationStatus = () => {
  const [currentStatus, setCurrentStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [notificationStatus, setNotificationStatus] = useState<{
    received: boolean;
    approved?: boolean;
    rejected?: boolean;
  }>({ received: false });
  const navigate = useNavigate();
  const location = useLocation();
  const [statusError, setStatusError] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<string>('');
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [emailResent, setEmailResent] = useState(false);

  useEffect(() => {
  const searchParams = new URLSearchParams(location.search);
  const applicantEmail = location.state?.email || searchParams.get('email') || '';
  const appId = searchParams.get('applicationId') || '';
  
  setEmail(applicantEmail);
  setApplicationId(appId);

  if (applicantEmail) {
    fetchStatus(applicantEmail);
    checkNotificationStatus(applicantEmail);
  }
}, [location]);

  useEffect(() => {
    if (currentStatus === 'pending' && email) {
      const interval = setInterval(() => {
        fetchStatus(email);
        checkNotificationStatus(email);
      }, 300000); // Check every 5 minutes
      
      return () => clearInterval(interval);
    }
  }, [currentStatus, email]);

  const fetchStatus = async (applicantEmail: string) => {
  try {
    setIsLoading(true);
    setStatusError(null);
    
    let response: ApplicationStatusResponse;
    
    if (applicationId) {
      // If we have application ID, use it for more accurate status
      response = await getApplicationStatus(applicationId);
    } else {
      // Fallback to email-based lookup
      response = await checkApplicationStatus(applicantEmail);
    }
    
    setCurrentStatus(response.application_status || 'pending');
    setLastUpdated(
      response.status_updated_at 
        ? new Date(response.status_updated_at).toLocaleString() 
        : ''
    );
  } catch (err: any) {
    console.error('Error fetching status:', err);
    setStatusError(err.message || 'Failed to fetch application status');
  } finally {
    setIsLoading(false);
  }
};

  const checkNotificationStatus = async (applicantEmail: string) => {
    try {
      const response = await axios.get<NotificationCheckResponse>(
        `${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/notifications/check?email=${encodeURIComponent(applicantEmail)}&type=application_received`
      );
      setNotificationStatus(prev => ({
        ...prev,
        received: response.data.exists
      }));
    } catch (err) {
      console.error('Error checking notification:', err);
    }
  };

  const handleResendEmail = async () => {
    if (!email) return;
    
    setIsResendingEmail(true);
    try {
      await resendApplicationEmail(email);
      setEmailResent(true);
      setNotificationStatus(prev => ({ ...prev, received: true }));
      
      // Show success message for 5 seconds
      setTimeout(() => {
        setEmailResent(false);
      }, 5000);
    } catch (err: any) {
      console.error('Error resending email:', err);
      alert('Failed to resend email. Please try again later.');
    } finally {
      setIsResendingEmail(false);
    }
  };


  const steps: StatusStep[] = [
    {
      id: 1,
      title: "Application Sent",
      description: notificationStatus.received 
        ? "✓ Confirmation email was sent to your address"
        : "Your application has been successfully submitted",
      status: 'completed',
      icon: notificationStatus.received ? MailCheck : CheckCircle,
      timestamp: "Just now"
    },
    {
      id: 2,
      title: "Application Review",
      description: "We are finalizing your registration in our system and will get back to you to complete the registration process",
      status: currentStatus === 'pending' ? 'current' : 'completed',
      icon: Clock,
      estimatedTime: "Within 48 hours"
    },
    {
      id: 3,
      title: currentStatus === 'approved' ? "Registration Ready" : 
             currentStatus === 'rejected' ? "Registration Not Available" : "Registration Processing",
      description: currentStatus === 'approved' ? "Welcome to the SkillsBridge community!" :
                 currentStatus === 'rejected' ? "We appreciate your interest in our program" :
                 "We'll notify you once your registration is ready",
      status: currentStatus !== 'pending' ? 'completed' : 'upcoming',
      icon: currentStatus === 'approved' ? Users : 
            currentStatus === 'rejected' ? XCircle : AlertCircle,
      estimatedTime: currentStatus !== 'pending' ? lastUpdated : "After review"
    }
  ];

  const getStatusConfig = () => {
    const config = {
      pending: {
        color: 'bg-blue-100 text-blue-800',
        icon: <Clock className="w-8 h-8 text-blue-500" />,
        title: "Registration Processing",
        description: "We are finalizing your registration in our system. This typically takes 2-3 business days.",
      },
      approved: {
        color: 'bg-green-100 text-green-800',
        icon: <CheckCircle className="w-8 h-8 text-green-500" />,
        title: "Registration Complete!",
        description: "Congratulations! Your registration is ready and you can now volunteer for our projects.",
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        icon: <XCircle className="w-8 h-8 text-red-500" />,
        title: "Registration Not Available",
        description: "We appreciate your interest but cannot complete your registration at this time.",
      }
    };
    return config[currentStatus] || config.pending;
  };

  const statusConfig = getStatusConfig();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
if (statusError) {
  return (
    <div className="application-status-container">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-600">Error Loading Application Status</h2>
          <p className="mt-2 text-gray-600">{statusError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}
return (
  <div className="application-status-container">
    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 p-6 text-white">
        <div className="flex items-center space-x-3">
          <Heart className="w-10 h-10" fill="currentColor" />
          <div>
            <h1 className="text-3xl font-bold">Application Status</h1>
            <p className="mt-2">Track your volunteer application progress</p>
          </div>
        </div>
      </div>
      {/* Status Summary */}
      <div className={`p-6 flex items-start space-x-4 ${
        currentStatus === 'approved' ? 'bg-green-100 text-green-800' :
        currentStatus === 'rejected' ? 'bg-red-100 text-red-800' :
        'bg-blue-100 text-blue-800'
      }`}>
        {statusConfig.icon}
        <div>
          <h2 className="text-xl font-semibold">{statusConfig.title}</h2>
          <p className="mt-1">{statusConfig.description}</p>
          {lastUpdated && (
            <p className="text-sm mt-2 opacity-80">
              Last updated: {lastUpdated}
            </p>
          )}
        </div>
      </div>
      {/* Notification Status */}
      {notificationStatus.received ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mx-6 mb-6 flex items-center">
          <MailCheck className="w-5 h-5 text-green-600 mr-2" />
          {emailResent ? (
            <span className="text-green-800">Email has been resent successfully!</span>
          ) : (
            <span className="text-green-800">We've sent a confirmation to your email</span>
          )}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mx-6 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <span className="text-yellow-800">Didn't receive an email?</span>
          </div>
          <button
            onClick={handleResendEmail}
            disabled={isResendingEmail}
            className="ml-4 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isResendingEmail ? (
              <>
                <Clock className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Resend Email
              </>
            )}
          </button>
        </div>
      )}

      {/* Steps */}
      <div className="p-6 border-b">
        <div className="relative">
          {/* Timeline */}
          <div className="progress-line">
            <div 
              className="progress-fill"
              style={{ 
                backgroundColor: currentStatus === 'approved' ? '#10b981' : 
                              currentStatus === 'rejected' ? '#ef4444' : '#3b82f6',
                height: `${currentStatus === 'pending' ? '50%' : '100%'}` 
              }}
            ></div>
          </div>

          {/* Steps */}
          <div className="space-y-8">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="step-item">
                  <div className={`step-icon ${
                    step.status === 'completed' ? 'completed' :
                    step.status === 'current' ? 'current' : 'pending'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className={`step-content ${
                    step.status === 'completed' ? 'completed-card' :
                    step.status === 'current' ? 'current-card' : 'pending-card'
                  }`}>
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold">{step.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        step.status === 'completed' ? 'bg-green-100 text-green-800' :
                        step.status === 'current' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {step.status === 'completed' ? 'Completed' :
                         step.status === 'current' ? 'In Progress' : 'Pending'}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{step.description}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      {step.timestamp && <span>✓ {step.timestamp}</span>}
                      {step.estimatedTime && !step.timestamp && (
                        <span>🕒 {step.estimatedTime}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-6">
        {currentStatus === 'approved' ? (
  <div>
    <button
      onClick={() => {
        navigate(`/signup?applicationId=${applicationId}`);
      }}
      className="w-full bg-green-600 hover:bg-green-700 text-white py-4 px-8 rounded-lg font-medium transition-colors text-lg md:py-6 md:px-12 md:text-xl"
    >
      Complete Your Registration
    </button>
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-600 mb-2">Already have an account?</p>
      <button
        onClick={() => navigate('/login')}
        className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
      >
        Login to your volunteer dashboard
      </button>
    </div>
  </div>

        ) : currentStatus === 'rejected' ? (
          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Explore Other Opportunities
            </button>
          </div>
        ) : (
          <div className="text-center text-gray-600">
            <p>We'll notify you at {email} when your registration is ready.</p>
          </div>
        )}

        <div className="back-button">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to previous page
          </button>
        </div>
      </div>
    </div>
  </div>
);
}


export default ApplicationStatus;